import type { TextZone, Cover } from '@/lib/api';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import { EDITOR_TEXT_FONTS, DEFAULT_COVER_FONT } from '@/lib/editorFonts';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const ZONE_TYPE_LABELS: Record<TextZone['type'], string> = {
  destination: 'Destination',
  year: 'Année',
  custom: 'Texte libre',
};

const ZONE_TYPE_DOT: Record<TextZone['type'], string> = {
  destination: 'bg-blue-500',
  year: 'bg-amber-500',
  custom: 'bg-purple-500',
};

// Default insertion positions match the worker /covers/from-extracted template
// so newly-added zones land where the AI extractor would have placed them.
const ZONE_DEFAULTS: Record<TextZone['type'], TextZone> = {
  destination: {
    type: 'destination',
    x: 0.10, y: 0.06, width: 0.83, height: 0.19,
    font_size: 410,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: 'DESTINATION',
  },
  year: {
    type: 'year',
    x: 0.70, y: 0.22, width: 0.18, height: 0.08,
    font_size: 170,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: '2024',
  },
  custom: {
    type: 'custom',
    x: 0.20, y: 0.45, width: 0.60, height: 0.10,
    font_size: 120,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: 'Texte',
  },
};

interface TextZoneEditorProps {
  /** The cover being edited — used for preview rendering only. */
  cover: Cover;
  /** Live background color (lets the parent's bg picker drive the preview). */
  backgroundColor: string;
  zones: TextZone[];
  onChange: (zones: TextZone[]) => void;
}

export function TextZoneEditor({ cover, backgroundColor, zones, onChange }: TextZoneEditorProps) {
  function updateZone(index: number, patch: Partial<TextZone>) {
    const next = [...zones];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  }

  function deleteZone(index: number) {
    onChange(zones.filter((_, i) => i !== index));
  }

  function addZone(type: TextZone['type']) {
    onChange([...zones, ZONE_DEFAULTS[type]]);
  }

  // Preview renders text using the zone's default_text — that's what customers
  // see in the picker before they type their own. Map zones back into the same
  // shape CoverThumbnail expects (destinationText / yearText / customTexts).
  const destZone = zones.find((z) => z.type === 'destination');
  const yearZone = zones.find((z) => z.type === 'year');
  const customTexts: Record<number, string> = {};
  zones.forEach((z, i) => {
    if (z.type === 'custom') customTexts[i] = z.default_text ?? '';
  });

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-5">
      {/* Live preview */}
      <div>
        <CoverThumbnail
          cover={{ ...cover, dominant_color: backgroundColor, text_zones: zones }}
          className="rounded-xl border border-gray-200"
          destinationText={destZone?.default_text ?? ''}
          yearText={yearZone?.default_text ?? ''}
          customTexts={customTexts}
          bgColorOverride={backgroundColor}
        />
        <p className="text-xs text-gray-500 mt-2">
          Aperçu avec les valeurs par défaut. La position est fixée — pour
          repositionner les zones, ré-uploadez le design.
        </p>
      </div>

      {/* Zone list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            Zones de texte ({zones.length})
          </h3>
        </div>

        {zones.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-400">
            Aucune zone. Ajoutez-en une ci-dessous.
          </div>
        ) : (
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
            {zones.map((zone, i) => (
              <ZoneCard
                key={i}
                zone={zone}
                onChange={(patch) => updateZone(i, patch)}
                onDelete={() => deleteZone(i)}
              />
            ))}
          </div>
        )}

        <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-2">
          <AddBtn onClick={() => addZone('destination')}>
            + Destination
          </AddBtn>
          <AddBtn onClick={() => addZone('year')}>
            + Année
          </AddBtn>
          <AddBtn onClick={() => addZone('custom')}>
            + Texte libre
          </AddBtn>
        </div>
      </div>
    </div>
  );
}

function ZoneCard({
  zone,
  onChange,
  onDelete,
}: {
  zone: TextZone;
  onChange: (patch: Partial<TextZone>) => void;
  onDelete: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-xl p-3 space-y-2.5 bg-white">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <span className={cn('w-2 h-2 rounded-full', ZONE_TYPE_DOT[zone.type])} />
          {ZONE_TYPE_LABELS[zone.type]}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-600 hover:text-red-700"
          title="Supprimer cette zone"
        >
          Supprimer
        </button>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Texte par défaut</label>
        <input
          type="text"
          value={zone.default_text ?? ''}
          onChange={(e) => onChange({ default_text: e.target.value })}
          placeholder={
            zone.type === 'destination' ? 'DESTINATION'
              : zone.type === 'year' ? '2024'
              : 'Texte'
          }
          className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Police</label>
        <select
          value={zone.font_family ?? DEFAULT_COVER_FONT.family}
          onChange={(e) => onChange({ font_family: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
        >
          {EDITOR_TEXT_FONTS.map((f) => (
            <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Taille (px)</label>
          <input
            type="number"
            min={6}
            max={1200}
            value={zone.font_size ?? 32}
            onChange={(e) => onChange({ font_size: parseInt(e.target.value, 10) || 32 })}
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Alignement</label>
          <select
            value={zone.align ?? 'center'}
            onChange={(e) => onChange({ align: e.target.value as TextZone['align'] })}
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          >
            <option value="left">Gauche</option>
            <option value="center">Centre</option>
            <option value="right">Droite</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Couleur</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={zone.color ?? '#ffffff'}
            onChange={(e) => onChange({ color: e.target.value })}
            className="w-10 h-8 rounded border border-gray-200 cursor-pointer"
          />
          <input
            type="text"
            value={zone.color ?? '#ffffff'}
            onChange={(e) => onChange({ color: e.target.value })}
            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm font-mono"
          />
        </div>
      </div>
    </div>
  );
}

function AddBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <Button variant="secondary" size="sm" onClick={onClick}>
      {children}
    </Button>
  );
}
